'use client'

import type { ButtonProps } from '@/components/ui/button'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { useTranslations } from 'next-intl'
import * as React from 'react'

const paginationIconClass =
  'size-8 min-w-8 shrink-0 rounded-lg border border-transparent font-medium tabular-nums'

const Pagination = ({
  className,
  ariaLabel,
  ...props
}: React.ComponentProps<'nav'> & { ariaLabel?: string }) => (
  <nav
    aria-label={ariaLabel ?? 'pagination'}
    className={cn('mx-auto flex w-full justify-center', className)}
    data-slot="pagination"
    role="navigation"
    {...props}
  />
)

const PaginationContent: React.FC<
  { ref?: React.Ref<HTMLUListElement> } & React.HTMLAttributes<HTMLUListElement>
> = ({ className, ref, ...props }) => (
  <ul
    className={cn('flex flex-row items-center gap-0.5', className)}
    data-slot="pagination-content"
    ref={ref}
    {...props}
  />
)

const PaginationItem: React.FC<
  { ref?: React.Ref<HTMLLIElement> } & React.HTMLAttributes<HTMLLIElement>
> = ({ className, ref, ...props }) => (
  <li className={cn('', className)} data-slot="pagination-item" ref={ref} {...props} />
)

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'button'>

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  type = 'button',
  ...props
}: PaginationLinkProps) => {
  const variant = isActive ? 'default' : 'ghost'

  return (
    <button
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        buttonVariants({ size, variant }),
        paginationIconClass,
        !isActive &&
          'hover:bg-muted hover:text-foreground dark:hover:bg-muted/50',
        isActive &&
          'border-transparent bg-primary text-primary-foreground shadow-none hover:bg-primary/90 hover:text-primary-foreground dark:bg-primary dark:hover:bg-primary/90',
        className,
      )}
      data-active={isActive}
      data-size={size}
      data-slot="pagination-link"
      data-variant={variant}
      type={type}
      {...props}
    />
  )
}

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => {
  const t = useTranslations('Pagination')
  return (
    <PaginationLink aria-label={t('prevAria')} className={className} size="icon" {...props}>
      <ChevronLeft className="h-4 w-4" aria-hidden />
    </PaginationLink>
  )
}

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => {
  const t = useTranslations('Pagination')
  return (
    <PaginationLink aria-label={t('nextAria')} className={className} size="icon" {...props}>
      <ChevronRight className="h-4 w-4" aria-hidden />
    </PaginationLink>
  )
}

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => {
  const t = useTranslations('Pagination')
  return (
    <span
      aria-hidden
      className={cn('flex size-8 items-center justify-center', className)}
      data-slot="pagination-ellipsis"
      {...props}
    >
      <MoreHorizontal className="h-4 w-4" />
      <span className="sr-only">{t('morePages')}</span>
    </span>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
}
